/**
 * Opciones del botón "Crear nuevo".
 *
 * Clásica: todos los tipos que el perfil puede crear.
 * Suite: solo los tipos de los flujos cuyo `suitemodule` es el del grupo abierto.
 * El id del tipo llega en el grupo (`requirementtypeids`), no por el nombre.
 */

export interface TipoCrear {
  id: number | string;
  name?: string;
  /** Etiqueta de la Suite. Vacío o ausente: se muestra el name. */
  alias?: string | null;
}

export interface GrupoParaCrear {
  id: number | string;
  name?: string;
  /** Etiqueta de la Suite. Vacío o ausente: se muestra el name del tipo. */
  alias?: string | null;
  suitemodule?: string | null;
  /** Tipos de requerimiento de ese flujo. Ausente = todavía no se conocen. */
  requirementtypeids?: Array<number | string> | null;
}

function aliasDeGrupo(grupo: { alias?: string | null } | null | undefined): string {
  return (grupo?.alias ?? '').trim();
}

export function tiposParaCrear(
  tipos: TipoCrear[] | null | undefined,
  grupos: GrupoParaCrear[] | null | undefined,
  grupoAbiertoId: number | string | null | undefined,
  modoSuite: boolean,
): TipoCrear[] {
  const lista = Array.isArray(tipos) ? tipos : [];
  if (!modoSuite) return lista;

  const gruposLista = Array.isArray(grupos) ? grupos : [];
  const abierto = gruposLista.find((g) => Number(g.id) === Number(grupoAbiertoId));
  const modulo = abierto?.suitemodule || null;
  if (!modulo) return [];
  return tiposDelModulo(lista, gruposLista, modulo);
}

/**
 * Tipos de los flujos de un `suitemodule` (p.ej. Todos los juicios → 'juicios').
 * Vacío mientras ningún flujo del módulo trae sus `requirementtypeids`.
 */
export function tiposDelModulo(
  tipos: TipoCrear[] | null | undefined,
  grupos: GrupoParaCrear[] | null | undefined,
  modulo: string,
): TipoCrear[] {
  const lista = Array.isArray(tipos) ? tipos : [];
  const gruposLista = Array.isArray(grupos) ? grupos : [];
  const ids = new Set<number>();
  let conocido = false;
  for (const grupo of gruposLista) {
    if (grupo.suitemodule !== modulo) continue;
    if (!Array.isArray(grupo.requirementtypeids)) continue;
    conocido = true;
    for (const id of grupo.requirementtypeids) {
      const n = Number(id);
      if (Number.isFinite(n)) ids.add(n);
    }
  }
  if (!conocido) return [];
  return lista.filter((tipo) => ids.has(Number(tipo.id)));
}

/** "Búsqueda en": clásica usa el name; la Suite usa el alias si tiene texto. */
export function etiquetaBusqueda(
  grupo: { name?: string; alias?: string | null } | null | undefined,
  modoSuite: boolean,
): string {
  const nombre = (grupo?.name ?? '').trim();
  if (!modoSuite) return nombre || 'Seleccione un Flujo';
  return aliasDeGrupo(grupo) || nombre || 'Seleccione un Flujo';
}

/** Etiqueta del tipo. En la Suite, alias si tiene texto. En la clásica, el name. */
export function etiquetaTipo(
  name: string | null | undefined,
  alias: string | null | undefined,
  modoSuite: boolean,
): string {
  const nombre = name ?? '';
  if (!modoSuite) return nombre;
  const texto = (alias ?? '').trim();
  return texto || nombre;
}

/**
 * Texto de "Crear nuevo" y del título del formulario.
 * Sale del alias del tipo. El name se sigue guardando y reconoce el contrato.
 */
export function etiquetaParaCrear(
  tipo: TipoCrear | null | undefined,
  modoSuite: boolean,
): string {
  if (!tipo) return '';
  return etiquetaTipo(tipo.name, tipo.alias, modoSuite);
}

/**
 * Columna Tipo de la grilla. El alias llega en la fila, cruzado por el id
 * del tipo en el API. El texto de la celda no decide la etiqueta.
 */
export function textoTipoGrilla(
  valor: unknown,
  alias: string | null | undefined,
  modoSuite: boolean,
): unknown {
  if (!modoSuite) return valor;
  const texto = (alias ?? '').trim();
  return texto || valor;
}

/**
 * Nombre del flujo en el título de la carga masiva. Con alias, la Suite muestra el alias.
 * Sin alias, el name real.
 */
export function etiquetaCargaVisible(
  grupo: { alias?: string | null } | null | undefined,
  modoSuite: boolean,
  etiquetaPorNombre: string,
): string {
  if (!modoSuite) return etiquetaPorNombre;
  return aliasDeGrupo(grupo) || etiquetaPorNombre;
}
